import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BaseReservation,
  RapidStorReservationRequest,
  ReservationPayload,
} from "@r6digital/rapidstor-types/types/Reservation";
import { options } from "../../services/rapidstor";
import store, { RootState } from "../../store";
import Location, { locationTransformer } from "./entities/Location";
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { EntityState } from "@reduxjs/toolkit/src/entities/models";
import Descriptor, { descriptorTransformer, UnitList } from "./entities/Descriptor";
import { unitTypeTransformer } from "./entities/UnitType";
import defaultLocations from "./defaultLocations";
import Site from "@r6digital/rapidstor-types/types/Site";
import {
  RapidStorResponse,
  RapidStorUnitList,
} from "@r6digital/rapidstor-types/types/Api";
import { Questionnaire } from "@r6digital/rapidstor-types/react/entities/Questionnaire";

export interface RapidStorPatchReservationRequest {
  reservationId: string;
  locationCode: string;
  payload: ReservationPayload;
}

const locationsAdapter = createEntityAdapter<Location>({
  selectId: (location) => location.locationCode,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});
const locationInitialState = locationsAdapter.getInitialState({
  ids: [],
  entities: defaultLocations
    .filter((location) => options.enabledLocations.includes(location.sLocationCode))
    .map((location) => locationTransformer(location)),
});

const baseQuery = fetchBaseQuery({
  baseUrl: options.baseURL,
});

export const CorpCode = "CDQS";

export const rapidstorApi = createApi({
  reducerPath: "rapidstorApi",
  baseQuery: baseQuery,
  keepUnusedDataFor: 3600,
  tagTypes: ["Location", "Questionnaire", "UnitList"],
  endpoints: (builder) => ({
    getLocations: builder.query<EntityState<Location>, void>({
      query: () => ({
        url: "/api/v1/locations",
        headers: {
          corp: CorpCode,
        },
      }),
      providesTags: ["Location"],
      transformResponse: (resp: RapidStorResponse<Site[]>) => {
        const locations = resp.results
          .map((location) => locationTransformer(location))
          .filter((location) => location.rapidstorEnabled)
          .filter((location) => options.enabledLocations.includes(location.locationCode));

        return locationsAdapter.setAll(locationInitialState, locations);
      },
    }),
    getQuestionnaire: builder.query<Questionnaire, string>({
      query: (locationCode) => ({
        url: `/api/v1/${locationCode}/questionnaire`,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "Questionnaire", id: result.locationCode }] : [],
      transformResponse: (resp: RapidStorResponse<Questionnaire>) => {
        return resp.results;
      },
    }),
    getUnitList: builder.query<UnitList, string>({
      query: (locationCode) => ({
        url: `/api/v1/locations/${locationCode}/unit-list`,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      providesTags: (result) => {
        return result ? [{ type: "UnitList", id: result.unitTypes[0].locationCode }] : [];
      },
      transformResponse: (resp: RapidStorResponse<RapidStorUnitList>): UnitList => {
        return {
          descriptors: resp.results.descriptors
            .map((d): Descriptor => {
              const location: Location | undefined = selectLocationByCode(
                store.getState(),
                d.sLocationCode
              );
              return descriptorTransformer(d, location);
            })
            .sort((a, b) => a.ordinalPosition - b.ordinalPosition)
            .filter((d) => d.unitTypes.length > 0 && !d.hidden && d.enabled),
          unitTypes: resp.results.unitTypes.map((u) => {
            const location: Location | undefined = selectLocationByCode(
              store.getState(),
              u.sLocationCode
            );
            return unitTypeTransformer(u, location);
          }),
        };
      },
    }),
    postReservation: builder.mutation<BaseReservation, RapidStorReservationRequest>({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/reservation`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    postSaveQuote: builder.mutation<BaseReservation, RapidStorReservationRequest>({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/reservation/save-quote`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    patchReservation: builder.mutation<BaseReservation, RapidStorPatchReservationRequest>(
      {
        query: ({ reservationId, locationCode, payload }) => ({
          url: `/api/v1/reservation/${reservationId}`,
          method: "PATCH",
          body: payload,
          headers: {
            site: locationCode,
            corp: CorpCode,
          },
        }),
        transformResponse: (resp: RapidStorResponse<BaseReservation>) => resp.results,
      }
    ),
  }),
});

export const { useGetLocationsQuery } = rapidstorApi;

const selectLocationsResult = rapidstorApi.endpoints.getLocations.select();
const selectLocationData = createSelector(selectLocationsResult, (locationResult) => {
  if (locationResult.status !== "fulfilled") {
    return locationsAdapter.setAll(
      locationInitialState,
      defaultLocations
        .filter((location) => options.enabledLocations.includes(location.sLocationCode))
        .map((l) => locationTransformer(l))
    );
  }
  return locationResult.data;
});

export const { selectAll: selectAllLocations, selectById: selectLocationByCode } =
  locationsAdapter.getSelectors(
    (state: RootState) => selectLocationData(state) ?? locationInitialState
  );

export const selectLocationsByState = createSelector(
  [selectAllLocations, (state, region?: string) => region],
  (locations, region) =>
    locations.filter((location) => (region ? location.address.region === region : true))
);

export const selectLocationBySlug = createSelector(
  [selectAllLocations, (state, slug: string) => slug],
  (locations, slug) => locations.find((location) => location.slug === slug)
);

export const {
  useGetUnitListQuery,
  usePostReservationMutation,
  usePostSaveQuoteMutation,
  useGetQuestionnaireQuery,
  usePatchReservationMutation,
} = rapidstorApi;
